// src/pages/CopyTrading/CopyTradingPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import { copyTradingService } from '../../services/copyTradingService';
import './CopyTradingPage.css';

// ── Currency helpers ───────────────────────────────────────────────────────────
const USD_SYMBOL = '$';
const NGN_SYMBOL = '₦';

const formatMoney = (displayValue, rawNgn, currency, usdRate) => {
  if (displayValue) return displayValue;
  const amount = parseFloat(rawNgn) || 0;
  if (currency === 'USD' && usdRate) {
    return `${USD_SYMBOL}${(amount * usdRate).toFixed(2)}`;
  }
  return `${NGN_SYMBOL}${amount.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const sym = (currency) => currency === 'USD' ? USD_SYMBOL : NGN_SYMBOL;

const toNgn = (value, currency, usdToNgnRate) => {
  const n = parseFloat(value) || 0;
  if (currency === 'USD' && usdToNgnRate) return n * usdToNgnRate;
  return n;
};

const fromNgn = (ngnValue, currency, usdRate) => {
  const n = parseFloat(ngnValue) || 0;
  if (currency === 'USD' && usdRate) return parseFloat((n * usdRate).toFixed(2));
  return n;
};

// ── Animated copy icon (top-left) - EXACT MATCH from your code ─────────────────
const CopyAnimation = () => {
  return (
    <div className="ct-copy-animation">
      {/* shimmer ring */}
      <div className="ct-shimmer-ring">
        <div className="ct-shimmer-ping" />
      </div>
      {/* back doc */}
      <svg className="ct-back-doc" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
      {/* front doc with shimmer */}
      <svg className="ct-front-doc" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <defs>
          <linearGradient id="copyGold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#b8860b" />
            <stop offset="100%" stopColor="#c8a020" />
          </linearGradient>
        </defs>
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="url(#copyGold)" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="url(#copyGold)" />
      </svg>
      {/* sparkle dots */}
      <span className="ct-sparkle-1" />
      <span className="ct-sparkle-2" />
      <span className="ct-sparkle-3" />
    </div>
  );
};

// ── Mini Logo Loader ───────────────────────────────────────────────────────────
const MiniLogoLoader = ({ label }) => (
  <div className="mini-loader-wrap">
    <div className="mini-logo">
      <div className="mini-bar-wrap mini-bar-left">
        <div className="mini-bar-fill mini-fill-left" />
      </div>
      <div className="mini-bar-wrap mini-bar-center">
        <div className="mini-bar-fill mini-fill-center" />
      </div>
      <div className="mini-bar-wrap mini-bar-right">
        <div className="mini-bar-fill mini-fill-right" />
      </div>
    </div>
    {label && <p className="mini-loader-label">{label}</p>}
  </div>
);

// ── Status Badge ───────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    // Follower/Copier relationship statuses
    ACTIVE:   { label: 'Active',   cls: 'badge-green'  },
    PAUSED:   { label: 'Paused',   cls: 'badge-yellow' },
    STOPPED:  { label: 'Stopped',  cls: 'badge-gray'   },
    REVOKED:  { label: 'Revoked',  cls: 'badge-red'    },
    // Trade execution statuses
    EXECUTED: { label: 'Executed', cls: 'badge-green'  },
    SKIPPED:  { label: 'Skipped',  cls: 'badge-yellow' },
    FAILED:   { label: 'Failed',   cls: 'badge-red'    },
    PENDING:  { label: 'Pending',  cls: 'badge-gray'   },
  };
  const { label, cls } = map[status] || { label: status, cls: 'badge-gray' };
  return <span className={`ct-badge ${cls}`}>{label}</span>;
};

// ── Pill Tabs ──────────────────────────────────────────────────────────────────
const Tabs = ({ tabs, active, onChange }) => (
  <div className="ct-tabs">
    {tabs.map(t => (
      <button
        key={t.id}
        className={`ct-tab ${active === t.id ? 'ct-tab-active' : ''}`}
        onClick={() => onChange(t.id)}
      >
        {t.label}
        {t.count > 0 && <span className="ct-tab-count">{t.count}</span>}
      </button>
    ))}
  </div>
);

// ── Risk Disclosure ────────────────────────────────────────────────────────────
const RiskDisclosure = ({ onAccept }) => (
  <div className="ct-risk-overlay">
    <div className="ct-risk-box">
      <div className="ct-risk-icon">⚠️</div>
      <h2>Copy Trading Risk Disclosure</h2>
      <div className="ct-risk-body">
        <p>Before using copy trading, please read and understand the following:</p>
        <ul>
          <li>Copy trading involves <strong>real financial risk</strong>. You may lose some or all of your investment.</li>
          <li>Past performance of a leader <strong>does not guarantee</strong> future results.</li>
          <li>Trades are mirrored <strong>automatically</strong> — you may not be able to prevent a trade once it fires.</li>
          <li>A <strong>performance fee</strong> (up to 40%) is deducted from your profits on each sale.</li>
          <li>Your trade sizes are <strong>proportional</strong> to your wallet balance, which may differ from the leader's.</li>
          <li>If your wallet falls below the leader's minimum, copy trading will be <strong>automatically paused</strong>.</li>
          <li>You can stop copying at any time; existing positions remain <strong>yours to manage</strong>.</li>
        </ul>
      </div>
      <button className="ct-btn ct-btn-primary ct-risk-accept" onClick={onAccept}>
        I Understand — Continue
      </button>
    </div>
  </div>
);

// ── Trade History: mobile card + desktop table ─────────────────────────────────
const TradeHistorySection = ({ trades, title, countLabel }) => {
  const [visibleCount, setVisibleCount] = useState(5);

  if (!trades?.length) {
    return <div className="ct-empty">No copy trades yet.</div>;
  }

  const visible = trades.slice(0, visibleCount);
  const hasMore = trades.length > visibleCount;

  return (
    <>
      {/* Mobile cards */}
      <div className="ct-trade-cards">
        {visible.map(t => {
          const qty = parseFloat(t.copier_quantity ?? t.leader_quantity).toFixed(4);
          const isFailed  = t.status === 'FAILED';
          const isSkipped = t.status === 'SKIPPED';
          return (
            <div className="ct-trade-card" key={t.id}>
              <div className="ct-trade-card-top">
                <div className="ct-trade-card-left">
                  <span className="ct-trade-card-stock">{t.stock_symbol}</span>
                  <span className={`ct-side ${t.order_side.toLowerCase()}`}>{t.order_side}</span>
                </div>
                <StatusBadge status={t.status} />
              </div>
              {(isFailed || isSkipped) && t.skip_reason && (
                <div className="ct-skip-reason">{t.skip_reason}</div>
              )}
              {isFailed && !t.skip_reason && (
                <div className="ct-skip-reason">Unknown error</div>
              )}
              <div className="ct-trade-card-meta">
                <span>
                  <span className="ct-trade-card-meta-label">QTY</span>
                  <span className="ct-trade-card-meta-val">{qty}</span>
                </span>
                <span>
                  <span className="ct-trade-card-meta-label">PRICE</span>
                  <span className="ct-trade-card-meta-val">{t.price_display}</span>
                </span>
                <span>
                  <span className="ct-trade-card-meta-label">FEE</span>
                  <span className="ct-trade-card-meta-val">{t.performance_fee_display}</span>
                </span>
              </div>
              <div className="ct-trade-card-date">
                {new Date(t.created_at).toLocaleDateString()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="ct-table-wrap ct-hide-mobile">
        <table className="ct-table">
          <thead>
            <tr>
              <th>STOCK</th>
              <th>SIDE</th>
              <th>QTY</th>
              <th>PRICE</th>
              <th>STATUS</th>
              <th>FEE PAID</th>
              <th>DATE</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(t => (
              <tr key={t.id}>
                <td className="ct-td-bold">
                  {t.stock_symbol}
                  {t.stock_name && <><br /><small style={{color:'#888',fontWeight:400}}>{t.stock_name}</small></>}
                </td>
                <td>
                  <span className={`ct-side ${t.order_side.toLowerCase()}`}>{t.order_side}</span>
                </td>
                <td>{parseFloat(t.copier_quantity ?? t.leader_quantity).toFixed(4)}</td>
                <td>{t.price_display}</td>
                <td>
                  <StatusBadge status={t.status} />
                  {t.skip_reason && <div className="ct-skip-reason">{t.skip_reason}</div>}
                  {t.status === 'FAILED' && !t.skip_reason && (
                    <div className="ct-skip-reason">Unknown error</div>
                  )}
                </td>
                <td>{t.performance_fee_display}</td>
                <td>{new Date(t.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="ct-see-more-wrap">
          <button
            className="ct-see-more-btn"
            onClick={() => setVisibleCount(c => c + 5)}
          >
            See more ({trades.length - visibleCount} remaining)
          </button>
        </div>
      )}
    </>
  );
};

// ── Notification message formatter ─────────────────────────────────────────────
const FormatNotifMsg = ({ message }) => {
  if (!message) return null;
  const parts = message.split(
    /(BUY|SELL|[\$₦][0-9,\.]+(?:\.[0-9]+)?|\(copying [^)]+\))/g
  );
  return (
    <span>
      {parts.map((part, i) => {
        if (part === 'BUY')  return <span key={i} className="ct-notif-buy">{part}</span>;
        if (part === 'SELL') return <span key={i} className="ct-notif-sell">{part}</span>;
        if (/^[\$₦]/.test(part)) return <span key={i} className="ct-notif-money">{part}</span>;
        if (/^\(copying/.test(part)) return <span key={i} className="ct-notif-leader">{part}</span>;
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
};

// ── Leader Panel ───────────────────────────────────────────────────────────────
const LeaderPanel = ({ profile, followers, trades, onUpdateProfile, onRegenCode, currency, usdRate, usdToNgnRate }) => {
  const [fee, setFee]       = useState(profile?.performance_fee_pct ?? 10);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [activeFollowerTab, setActiveFollowerTab] = useState('followers');
  const [visibleTradesCount, setVisibleTradesCount] = useState(5);

  const [minBal, setMinBal] = useState(
    fromNgn(profile?.minimum_copier_balance ?? 10000, currency, usdRate)
  );

  useEffect(() => {
    setMinBal(fromNgn(profile?.minimum_copier_balance ?? 10000, currency, usdRate));
  }, [currency, usdRate, profile?.minimum_copier_balance]);

  const handleSave = async () => {
    if (fee < 0 || fee > 40) { setMsg('Fee must be between 0% and 40%.'); return; }
    if (minBal < 0) { setMsg('Minimum balance cannot be negative.'); return; }
    setSaving(true);
    try {
      const minBalNgn = toNgn(minBal, currency, usdToNgnRate);
      await onUpdateProfile({ performance_fee_pct: fee, minimum_copier_balance: minBalNgn });
      setMsg('Settings saved!');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const handleToggleActive = async () => {
    setSaving(true);
    try {
      await onUpdateProfile({ is_active: !profile.is_active });
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  const zeroDisplay = currency === 'USD' ? `${USD_SYMBOL}0.00` : `${NGN_SYMBOL}0.00`;

  return (
    <div className="ct-panel">

      {/* Code card */}
      <div className="ct-code-card">
        <div className="ct-code-badge">
          <span className="ct-code-crown">👑</span>
          <span className="ct-code-label">YOUR COPY TRADING CODE</span>
        </div>
        <div className="ct-code-value">{profile?.code}</div>
        <div className="ct-code-hint">Share this code with others so they can mirror your trades.</div>
        <div className="ct-code-actions">
          <button className="ct-btn ct-btn-outline" onClick={() => navigator.clipboard?.writeText(profile?.code)}>
            📋 Copy Code
          </button>
          <button className="ct-btn-gold" onClick={onRegenCode}>
            🔄 New Code
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="ct-stats-row">
        <div className="ct-stat">
          <div className="ct-stat-icon ct-stat-icon-green">👥</div>
          <div className="ct-stat-label">Active Followers</div>
          <div className="ct-stat-value">{profile?.active_followers_count ?? 0}</div>
        </div>
        <div className="ct-stat">
          <div className="ct-stat-icon ct-stat-icon-green">👤</div>
          <div className="ct-stat-label">Total Followers</div>
          <div className="ct-stat-value">{profile?.total_followers ?? 0}</div>
        </div>
        <div className="ct-stat">
          <div className="ct-stat-icon ct-stat-icon-gold">💼</div>
          <div className="ct-stat-label">Fees Earned</div>
          <div className="ct-stat-value ct-stat-value-gold">
            {profile?.total_fees_earned_display ?? zeroDisplay}
          </div>
        </div>
      </div>

      {/* Leader Settings */}
      <div className="ct-section">
        <div className="ct-settings-header">
          <div className="ct-settings-icon">🛡️</div>
          <div className="ct-settings-header-text">
            <h3>Leader Settings</h3>
            <p>Tune your terms for new copiers.</p>
          </div>
        </div>

        <div className="ct-form-row">
          <label className="ct-label">Performance Fee (%)</label>
          <span className="ct-hint">Max 40%. Taken from copier's profit on each sale.</span>
          <div className="ct-input-row">
            <input
              type="number" min="0" max="40" step="0.5"
              value={fee}
              onChange={e => setFee(parseFloat(e.target.value) || 0)}
              className="ct-input"
            />
            <span className="ct-input-unit">%</span>
          </div>
        </div>

        <div className="ct-form-row">
          <label className="ct-label">Minimum Copier Wallet Balance ({sym(currency)})</label>
          <span className="ct-hint">
            Copier must have at least this much in their wallet to follow you.
            {currency === 'USD' && ' Value is stored in NGN — converted automatically.'}
          </span>
          <div className="ct-input-row">
            <span className="ct-input-prefix">{sym(currency)}</span>
            <input
              type="number"
              min="0"
              step={currency === 'USD' ? '10' : '1000'}
              value={minBal}
              onChange={e => setMinBal(parseFloat(e.target.value) || 0)}
              className="ct-input ct-input-prefix-pad"
            />
          </div>
        </div>

        {msg && (
          <div className={`ct-msg ${msg.includes('saved') ? 'ct-msg-success' : 'ct-msg-error'}`}>
            {msg}
          </div>
        )}

        <div className="ct-form-actions">
          <button className="ct-btn ct-btn-primary" onClick={handleSave} disabled={saving}>
            ✓ {saving ? 'Saving…' : 'Save Settings'}
          </button>
          <button
            className={`ct-btn ${profile?.is_active ? 'ct-btn-danger-outline' : 'ct-btn-success'}`}
            onClick={handleToggleActive}
            disabled={saving}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
              <path d="M12 2v10" />
              <path d="M18.4 6.6a9 9 0 1 1-12.8 0" />
            </svg>
            {profile?.is_active ? 'Deactivate Copy Trading' : 'Activate Copy Trading'}
          </button>
        </div>
      </div>

      {/* Followers / Triggered Trades */}
      <div className="ct-section">
        <Tabs
          tabs={[
            { id: 'followers', label: 'Followers', count: followers?.length ?? 0 },
            { id: 'trades',    label: 'Triggered Trades', count: 0 },
          ]}
          active={activeFollowerTab}
          onChange={(id) => { setActiveFollowerTab(id); setVisibleTradesCount(5); }}
        />

        {activeFollowerTab === 'followers' && (
          <div className="ct-table-wrap">
            {!followers?.length ? (
              <div className="ct-empty">No followers yet. Share your code to get started.</div>
            ) : (
              <table className="ct-table">
                <thead>
                  <tr>
                    <th>Copier</th>
                    <th>Status</th>
                    <th>Balance</th>
                    <th>Fee %</th>
                    <th>Trades</th>
                    <th>Profit (theirs)</th>
                    <th>Fees Paid</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {followers.map(f => (
                    <tr key={f.id}>
                      <td className="ct-td-bold">{f.copier_username}</td>
                      <td><StatusBadge status={f.status} /></td>
                      <td>{f.copier_wallet_balance_display}</td>
                      <td>{f.agreed_performance_fee_pct}%</td>
                      <td>{f.total_copy_trades}</td>
                      <td>{f.total_profit_generated_display}</td>
                      <td>{f.total_fees_paid_display}</td>
                      <td>{new Date(f.joined_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeFollowerTab === 'trades' && (
          <div className="ct-table-wrap">
            {!trades?.length ? (
              <div className="ct-empty">No copy trades triggered yet.</div>
            ) : (
              <table className="ct-table">
                <thead>
                  <tr>
                    <th>Stock</th>
                    <th>Side</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Fee Earned</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.slice(0, visibleTradesCount).map(t => (
                    <tr key={t.id}>
                      <td className="ct-td-bold">{t.stock_symbol}</td>
                      <td>
                        <span className={`ct-side ${t.order_side.toLowerCase()}`}>{t.order_side}</span>
                      </td>
                      <td>{t.order_type}</td>
                      <td><StatusBadge status={t.status} /></td>
                      <td>{t.performance_fee_display}</td>
                      <td>{new Date(t.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {trades?.length > visibleTradesCount && (
              <div className="ct-see-more-wrap">
                <button
                  className="ct-see-more-btn"
                  onClick={() => setVisibleTradesCount(c => c + 5)}
                >
                  See more ({trades.length - visibleTradesCount} remaining)
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Copier Panel ───────────────────────────────────────────────────────────────
const CopierPanel = ({ relationship, trades, onFollow, onStop, currency }) => {
  const [code, setCode]             = useState('');
  const [preview, setPreview]       = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]               = useState('');

  const isFollowing = relationship?.status === 'ACTIVE';
  const isPaused    = relationship?.status === 'PAUSED';

  const zeroDisplay = currency === 'USD' ? `${USD_SYMBOL}0.00` : `${NGN_SYMBOL}0.00`;

  const handlePreview = async () => {
    if (!code.trim()) return;
    setPreviewing(true);
    setPreviewError('');
    setPreview(null);
    try {
      const data = await copyTradingService.previewCode(code);
      setPreview(data);
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleFollow = async () => {
    setSubmitting(true);
    setMsg('');
    try {
      await onFollow(code);
      setCode('');
      setPreview(null);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm('Stop copying? Your existing positions remain yours.')) return;
    setSubmitting(true);
    try {
      await onStop();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ct-panel">

      {/* Active/Paused copy card */}
      {(isFollowing || isPaused) && relationship && (
        <div className={`ct-active-card ${isPaused ? 'ct-active-card-paused' : ''}`}>
          <div className="ct-active-header">
            <div className="ct-active-header-left">
              <div className="ct-active-icon">✓</div>
              <div>
                <div className="ct-active-title">
                  {isPaused ? '⏸ Copy Trading Paused' : 'Copying'}
                </div>
                <div className="ct-active-leader">
                  Leader: <strong>{relationship.leader_username}</strong>
                  <span className="ct-leader-code">{relationship.leader_code}</span>
                </div>
              </div>
            </div>
            <span className="ct-badge-active-pill">
              {isPaused ? 'PAUSED' : 'ACTIVE'}
            </span>
          </div>

          {isPaused && (
            <div className="ct-paused-hint">
              Copy trading is paused because your wallet balance is below this
              leader's minimum. Top up your wallet to resume automatically.
            </div>
          )}

          <div className="ct-rel-stats">
            <div className="ct-rel-stat">
              <div className="ct-rel-stat-value">{relationship.total_copy_trades}</div>
              COPY TRADES
            </div>
            <div className="ct-rel-stat">
              <div className="ct-rel-stat-value ct-rel-stat-value-green">
                {relationship.total_profit_generated_display ?? zeroDisplay}
              </div>
              TOTAL PROFIT
            </div>
            <div className="ct-rel-stat">
              <div className="ct-rel-stat-value">
                {relationship.total_fees_paid_display ?? zeroDisplay}
              </div>
              FEES PAID
            </div>
            <div className="ct-rel-stat">
              <div className="ct-rel-stat-value ct-rel-stat-value-gold">
                {relationship.agreed_performance_fee_pct}%
              </div>
              YOUR FEE RATE
            </div>
          </div>

          <button className="ct-btn ct-btn-danger" onClick={handleStop} disabled={submitting}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
              <path d="M12 2v10" />
              <path d="M18.4 6.6a9 9 0 1 1-12.8 0" />
            </svg>
            Stop Copying
          </button>
        </div>
      )}

      {/* Follow a leader */}
      {!isFollowing && !isPaused && (
        <div className="ct-section">
          <h3 className="ct-section-title">Follow a Leader</h3>
          <p className="ct-section-desc">
            Enter the copy trading code shared by a leader. Their trades will be
            automatically mirrored in your account, proportional to your wallet.
          </p>

          <div className="ct-code-entry">
            <input
              type="text"
              placeholder="e.g. CT-A3F9KX"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              className="ct-input ct-code-input"
              maxLength={9}
            />
            <button
              className="ct-btn ct-btn-outline"
              onClick={handlePreview}
              disabled={previewing || !code.trim()}
            >
              {previewing ? 'Checking…' : 'Preview'}
            </button>
          </div>

          {previewError && <div className="ct-msg ct-msg-error">{previewError}</div>}

          {preview && (
            <div className="ct-preview-card">
              <div className="ct-preview-title">Leader Preview</div>
              <div className="ct-preview-rows">
                <div className="ct-preview-row">
                  <span>Leader</span>
                  <strong>{preview.leader_username}</strong>
                </div>
                <div className="ct-preview-row">
                  <span>Performance Fee</span>
                  <strong>{preview.performance_fee_pct}%</strong>
                </div>
                <div className="ct-preview-row">
                  <span>Min. Wallet Required</span>
                  <strong>{preview.minimum_copier_balance_display}</strong>
                </div>
                <div className="ct-preview-row">
                  <span>Active Followers</span>
                  <strong>{preview.active_followers_count}</strong>
                </div>
              </div>

              <div className="ct-risk-mini">
                ⚠️ Trades will be executed automatically on your behalf. Performance fees are deducted from profits only.
              </div>

              {msg && <div className="ct-msg ct-msg-error">{msg}</div>}

              <button className="ct-btn ct-btn-primary" onClick={handleFollow} disabled={submitting}>
                {submitting ? 'Submitting…' : `Follow ${preview.leader_username}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Copy trade history */}
      <div className="ct-section">
        <div className="ct-history-header">
          <span className="ct-history-title">My Copy Trade History</span>
          {trades?.length > 0 && (
            <span className="ct-history-count">{trades.length} total</span>
          )}
        </div>
        <TradeHistorySection trades={trades} />
      </div>
    </div>
  );
};

// ── Notifications Panel ────────────────────────────────────────────────────────
const NotificationsPanel = ({ notifications, unreadCount, onMarkAllRead }) => {
  const [filter, setFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(5);

  const handleFilterChange = (f) => {
    setFilter(f);
    setVisibleCount(5);
  };

  const filtered = notifications.filter(n => {
    if (filter === 'executed') return n.message?.toLowerCase().includes('executed');
    if (filter === 'failed')   return n.message?.toLowerCase().includes('failed');
    return true;
  });

  const visible   = filtered.slice(0, visibleCount);
  const hasMore   = filtered.length > visibleCount;

  const activeClass = { all: 'ct-notif-filter-all-active', executed: 'ct-notif-filter-executed-active', failed: 'ct-notif-filter-failed-active' };

  return (
    <div className="ct-panel">
      <div className="ct-section">
        <div className="ct-notif-header">
          <div className="ct-notif-icon">🔔</div>
          <div className="ct-notif-header-text">
            <h3>Notifications</h3>
            <p>Activity from your copied leaders.</p>
          </div>
          {unreadCount > 0 && (
            <button
              className="ct-btn ct-btn-outline ct-btn-sm"
              onClick={onMarkAllRead}
              style={{ marginLeft: 'auto', flexShrink: 0 }}
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="ct-notif-filters">
          {[
            { key: 'all',      label: 'All'      },
            { key: 'executed', label: 'Executed' },
            { key: 'failed',   label: 'Failed'   },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`ct-notif-filter ${filter === key ? activeClass[key] : ''}`}
              onClick={() => handleFilterChange(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {!filtered.length ? (
          <div className="ct-empty">No notifications yet.</div>
        ) : (
          <>
            <div className="ct-notif-list">
              {visible.map(n => {
                const isFail = n.message?.toLowerCase().includes('failed');
                return (
                  <div key={n.id} className="ct-notif">
                    <div className={`ct-notif-dot ${isFail ? 'ct-notif-dot-fail' : 'ct-notif-dot-success'}`}>
                      {isFail ? '✕' : '✓'}
                    </div>
                    <div className="ct-notif-body">
                      <div className="ct-notif-msg">
                        <FormatNotifMsg message={n.message} />
                      </div>
                      <div className="ct-notif-date">
                        {new Date(n.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div className="ct-see-more-wrap">
                <button
                  className="ct-see-more-btn"
                  onClick={() => setVisibleCount(c => c + 5)}
                >
                  See more ({filtered.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const CopyTradingPage = () => {
  const { currency, exchangeRate } = useCurrency();

  const usdRate      = exchangeRate?.ngn_to_usd ?? null;
  const usdToNgnRate = exchangeRate?.usd_to_ngn ?? null;

  const [riskAccepted, setRiskAccepted] = useState(
    () => localStorage.getItem('ct_risk_accepted') === '1'
  );
  const [tab, setTab] = useState('leader');

  const [profile, setProfile]             = useState(null);
  const [followers, setFollowers]         = useState([]);
  const [leaderTrades, setLeaderTrades]   = useState([]);
  const [relationship, setRelationship]   = useState(null);
  const [copyTrades, setCopyTrades]       = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        profileData,
        followersData,
        leaderTradesData,
        statusData,
        copyTradesData,
        notifsData,
      ] = await Promise.all([
        copyTradingService.getMyProfile(),
        copyTradingService.getMyFollowers(),
        copyTradingService.getLeaderCopyTrades(),
        copyTradingService.getMyCopyStatus(),
        copyTradingService.getMyCopyTrades(),
        copyTradingService.getNotifications(),
      ]);

      setProfile(profileData);
      setFollowers(Array.isArray(followersData) ? followersData : []);
      setLeaderTrades(Array.isArray(leaderTradesData) ? leaderTradesData : []);
      setRelationship(statusData.relationship);
      setCopyTrades(Array.isArray(copyTradesData) ? copyTradesData : []);
      const n = Array.isArray(notifsData) ? notifsData : [];
      setNotifications(n);
      setUnreadCount(n.filter(x => !x.is_read).length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currency]);

  useEffect(() => { load(); }, [load]);

  const handleAcceptRisk = () => {
    localStorage.setItem('ct_risk_accepted', '1');
    setRiskAccepted(true);
  };

  const handleUpdateProfile = async (data) => {
    const updated = await copyTradingService.updateProfile(data);
    setProfile(updated);
  };

  const handleRegenCode = async () => {
    if (!window.confirm('Generate a new code? Old code will stop working immediately.')) return;
    const { code } = await copyTradingService.regenerateCode();
    setProfile(prev => ({ ...prev, code }));
  };

  const handleFollow = async (code) => {
    const rel = await copyTradingService.followLeader(code);
    setRelationship(rel);
    load();
  };

  const handleStop = async () => {
    await copyTradingService.stopCopying();
    setRelationship(null);
    load();
  };

  const handleMarkAllRead = async () => {
    await copyTradingService.markNotificationsRead({ markAll: true });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
    window.dispatchEvent(new Event('refreshAlerts'));
  };

  if (!riskAccepted) return <RiskDisclosure onAccept={handleAcceptRisk} />;

  return (
    <div className="ct-page">

      {/* Hero header with animation */}
      <div className="ct-hero">
        {/* Animated copy icon - positioned to the left of SOCIAL TRADING */}
        <div className="ct-hero-animation-wrapper">
          <CopyAnimation />
        </div>

        <div className="ct-hero-eyebrow">
          <span className="ct-hero-eyebrow-star">✦</span>
          SOCIAL TRADING
        </div>
        <h1 className="ct-hero-title">Copy Trading</h1>
        <p className="ct-hero-sub">
          Mirror trades automatically or let others copy yours — track every fill, every fee, in real time.
        </p>
      </div>

      {/* Pill tabs */}
      <div className="ct-tabs-wrap">
        <Tabs
          tabs={[
            { id: 'leader',        label: 'My Leader Dashboard', count: 0 },
            { id: 'copier',        label: 'Copy a Leader',       count: 0 },
            { id: 'notifications', label: 'Notifications',       count: unreadCount },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {error && (
        <div style={{ padding: '0 16px' }}>
          <div className="ct-msg ct-msg-error" style={{ margin: '12px 0 0' }}>{error}</div>
        </div>
      )}

      <div className="ct-content">
        {loading ? (
          <MiniLogoLoader label="Loading copy trading data…" />
        ) : (
          <>
            {tab === 'leader' && (
              <LeaderPanel
                profile={profile}
                followers={followers}
                trades={leaderTrades}
                onUpdateProfile={handleUpdateProfile}
                onRegenCode={handleRegenCode}
                currency={currency}
                usdRate={usdRate}
                usdToNgnRate={usdToNgnRate}
              />
            )}

            {tab === 'copier' && (
              <CopierPanel
                relationship={relationship}
                trades={copyTrades}
                onFollow={handleFollow}
                onStop={handleStop}
                currency={currency}
              />
            )}

            {tab === 'notifications' && (
              <NotificationsPanel
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkAllRead={handleMarkAllRead}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CopyTradingPage;